/**
 * Distributor (DIST) accounts use a hardcoded Exgo window per season instead of the AMT season dates.
 * This is the single source of truth for that window, shared by the Excel order upload (web, web-next)
 * and the API (Order Book season grouping).
 *
 *   SSyy: Aug 1 of 20yy-1 until the last day of Feb of 20yy
 *   FWyy: Mar 1 of 20yy until Jul 31 of 20yy
 *
 * Pure and dependency free, so it is safe to use in the API and in the browser.
 */
export interface DistributorSeasonWindow {
  seasonName: string;
  /** Inclusive, YYYY-MM-DD. */
  earliest: string;
  /** Inclusive, YYYY-MM-DD. */
  latest: string;
}

const DISTRIBUTOR_SEASON_NAME_PATTERN = /^(SS|FW)(\d{2})$/;

const CALENDAR_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})(T.+)?$/;

const DISTRIBUTOR_WINDOW_BY_SEASON_TYPE = {
  SS: { earliestMonth: '08', earliestYearOffset: -1, nextWindowMonth: 3 },
  FW: { earliestMonth: '03', earliestYearOffset: 0, nextWindowMonth: 8 },
} as const;

const isRealCalendarDate = (year: number, month: number, day: number): boolean => {
  const utcDate = new Date(Date.UTC(year, month - 1, day));

  return utcDate.getUTCFullYear() === year && utcDate.getUTCMonth() === month - 1 && utcDate.getUTCDate() === day;
};

const getDayBeforeMonthStart = (year: number, month: number): string =>
  new Date(Date.UTC(year, month - 1, 0)).toISOString().slice(0, 10);

/**
 * Derives the distributor Exgo window from a season name.
 *
 * @param seasonName - Season name such as `SS27` or `FW27` (case-insensitive).
 * @returns The inclusive window, or `null` when the name is not `SS`/`FW` followed by two digits.
 */
export const getDistributorSeasonWindow = (seasonName: string | undefined): DistributorSeasonWindow | null => {
  const match = DISTRIBUTOR_SEASON_NAME_PATTERN.exec(seasonName?.trim().toUpperCase() ?? '');

  if (!match) {
    return null;
  }

  const seasonType = match[1] as keyof typeof DISTRIBUTOR_WINDOW_BY_SEASON_TYPE;
  const seasonYear = 2000 + Number(match[2]);
  const windowMonths = DISTRIBUTOR_WINDOW_BY_SEASON_TYPE[seasonType];

  return {
    seasonName: `${seasonType}${match[2]}`,
    earliest: `${seasonYear + windowMonths.earliestYearOffset}-${windowMonths.earliestMonth}-01`,
    latest: getDayBeforeMonthStart(seasonYear, windowMonths.nextWindowMonth),
  };
};

/**
 * Normalizes a stored requested receipt date to its calendar date.
 *
 * - `Date`: its UTC calendar date, since the API stores `dueDate` as UTC midnight.
 * - `YYYY-MM-DD` string: returned as is when it is a real calendar date.
 * - ISO timestamp string: the calendar date as written in its own offset.
 *
 * @returns The `YYYY-MM-DD` calendar date, or `null` when the value is missing or invalid.
 */
export const toCalendarDate = (date: Date | string | null | undefined): string | null => {
  if (date instanceof Date) {
    return Number.isNaN(date.getTime()) ? null : date.toISOString().slice(0, 10);
  }

  const trimmedDate = typeof date === 'string' ? date.trim() : '';
  const match = CALENDAR_DATE_PATTERN.exec(trimmedDate);
  const hasValidTime = match?.[4] === undefined || !Number.isNaN(Date.parse(trimmedDate));

  if (!match || !hasValidTime || !isRealCalendarDate(Number(match[1]), Number(match[2]), Number(match[3]))) {
    return null;
  }

  return `${match[1]}-${match[2]}-${match[3]}`;
};

const FW_WINDOW_START_MONTH_DAY = '03-01';
const SS_WINDOW_START_MONTH_DAY = '08-01';

const toSeasonName = (seasonType: 'SS' | 'FW', seasonYear: number): string | undefined =>
  seasonYear >= 2000 && seasonYear <= 2099 ? `${seasonType}${String(seasonYear - 2000).padStart(2, '0')}` : undefined;

/**
 * Season whose window owns a calendar date. Windows are contiguous and do not overlap: a season
 * starts the day after the previous one ends (Mar 1, Aug 1).
 */
const getCalendarOwnerSeasonName = (calendarDate: string): string | undefined => {
  const calendarYear = Number(calendarDate.slice(0, 4));
  const monthDay = calendarDate.slice(5);

  if (monthDay < FW_WINDOW_START_MONTH_DAY) {
    return toSeasonName('SS', calendarYear);
  }

  return monthDay < SS_WINDOW_START_MONTH_DAY ? toSeasonName('FW', calendarYear) : toSeasonName('SS', calendarYear + 1);
};

/**
 * Resolves the season owning a requested receipt date, but only when that season is one of the
 * candidates. The owner is determined from the calendar alone, so a date never falls back to a
 * neighbouring season when its real owner is not a candidate (for example Mar 1 2027 is FW27, not
 * SS27, even while FW27 is unknown).
 *
 * @param date - The requested receipt date.
 * @param seasonNames - Candidate season names; names without a distributor window are ignored.
 * @returns The owning season name as given in `seasonNames`, or `undefined` when the date is invalid
 * or its owning season is not a candidate.
 */
export const resolveDistributorSeasonName = (
  date: Date | string | null | undefined,
  seasonNames: string[],
): string | undefined => {
  const calendarDate = toCalendarDate(date);
  const ownerSeasonName = calendarDate ? getCalendarOwnerSeasonName(calendarDate) : undefined;

  return ownerSeasonName
    ? seasonNames.find((seasonName) => getDistributorSeasonWindow(seasonName)?.seasonName === ownerSeasonName)
    : undefined;
};
